/**
 * URL state for the matter-workspace AI panel.
 *
 * The query param is `?ai=briefing|analysis|chat|context-analysis|tasks|messages`.
 * When the param is absent the panel is closed.
 *
 * Backwards-compat: the legacy `?chatbox=true` flag (which only ever opened
 * the chat surface) is treated as `?ai=chat` on read so existing bookmarks
 * and links don't break. We intentionally do *not* rewrite the URL in
 * place — preserving the legacy param avoids a surprise navigation race
 * with code paths that still set `?chatbox=true` (PrimarySideBar, doc
 * navigation helpers, tree-component navigation). Once every writer has
 * been moved to `setAIPaneTab`, the legacy read can be deleted in a
 * follow-up cleanup.
 *
 * Mode awareness (Phase 5):
 * Different AIPane consumers expose different tab subsets.
 *   - Matter workspace shows `[analysis, chat]`. The "analysis" tab is
 *     labelled "The Brief" in the UI as of the 2026-04-29 rename — the
 *     URL value stays `analysis` so existing share links / bookmarks
 *     keep working. The standalone "Briefing" tab was removed from
 *     matter mode in the same change: attorneys only ever wanted the
 *     deep analysis report, the auto-briefing was a confusing parallel
 *     surface. The auto-briefing is still generated server-side
 *     (`DocumentBriefing` model) because the inbox uses it; matter
 *     mode just no longer surfaces it as its own tab.
 *   - Attorney inbox shows `[briefing, context-analysis, tasks, messages]`.
 *     Inbox keeps the briefing tab unchanged — it's the inbox's primary
 *     artifact (no deep analysis surface in inbox mode). No chat
 *     either — chat is matter-workspace-only.
 * The tab name `?ai=` value space is shared across modes (so a deep-link
 * to `?ai=briefing` is parseable everywhere), but `getValidTabsForMode`
 * / `clampTabToMode` enforce that an inbox URL with `?ai=chat` falls
 * back to the inbox default rather than rendering a missing tab — and a
 * matter-workspace URL with `?ai=tasks` or the legacy `?ai=briefing`
 * clamps to the matter default ("analysis" / "The Brief") for the same
 * reason.
 *
 * Tasks + Messages (Phase 5c) live behind `?ai=tasks` / `?ai=messages`
 * inbox-only tabs that lazy-fetch their respective per-doc lists from
 * the backend on first activation.
 */

export const AI_PANE_TABS = Object.freeze([
  "briefing",
  "analysis",
  "chat",
  "context-analysis",
  "tasks",
  "messages",
  "work",
]);
// Default landing tab when callers don't specify one. Matches the
// matter-mode first tab post-rename. Inbox callers go through
// `pickDefaultAIPaneTab(pathname, "inbox")` and never hit this fallback.
export const AI_PANE_DEFAULT_TAB = "analysis";

export const AI_PANE_MODE_MATTER = "matter";
export const AI_PANE_MODE_INBOX = "inbox";
// Chat-only matter workspace (VA CAVC): the matter is a single Record
// Before the Agency PDF conversed with via chat — there is no VA Agency
// knowledge graph behind it, so "The Brief" surface doesn't apply.
// Matthew (2026-07-16): CAVC matters show Chat alone, no tab strip.
export const AI_PANE_MODE_MATTER_CHAT_ONLY = "matter-chat-only";

/**
 * Tab list per AIPane mode. Order is the rendered tab order (left → right).
 *
 * Matter mode (post-2026-04-29 rename): the standalone "Briefing" tab was
 * removed in favour of a single "The Brief" surface (the renamed analysis
 * tab); see top-of-file rationale.
 *
 * Inbox mode is briefing-first so the user lands on the per-doc summary
 * by default, then pivots through the per-matter "fit into matter"
 * narrative, the assigned tasks list, and the per-doc message thread
 * without leaving the inbox.
 */
const TABS_BY_MODE = Object.freeze({
  // "work" (2026-08-28) is the matter Work panel — the WHG Docs twin of
  // the new Salesforce work rail. Matter mode only: the inbox surfaces
  // work items through its own waiting-on banner + attach flow.
  [AI_PANE_MODE_MATTER]: Object.freeze(["analysis", "chat", "work", "messages"]),
  [AI_PANE_MODE_MATTER_CHAT_ONLY]: Object.freeze(["chat"]),
  [AI_PANE_MODE_INBOX]: Object.freeze([
    "briefing",
    "context-analysis",
    "tasks",
    "messages",
  ]),
});

export const getValidTabsForMode = (mode) =>
  TABS_BY_MODE[mode] || TABS_BY_MODE[AI_PANE_MODE_MATTER];

/**
 * Per-tab capability requirement (2026-08-05 access-control rollout —
 * see `users/capabilities.py` in dms_backend). Tabs not listed here
 * (`tasks`, `messages`) are open to every authenticated user; `chat`
 * isn't listed because its requirement depends on whether a specific
 * document is active (see `filterTabsByCapability`).
 */
const CAPABILITY_BY_TAB = Object.freeze({
  analysis: "ai.brief.view",
  briefing: "ai.inbox_briefing",
  "context-analysis": "ai.inbox_briefing",
});

/**
 * Narrow a mode's tab list down to what `hasCapability` actually grants.
 *
 * VA CAVC matters (`AI_PANE_MODE_MATTER_CHAT_ONLY`) bypass this entirely
 * — chat is the whole workspace for that case type regardless of
 * capability, mirroring the backend's unconditional bypass in
 * `agent_proxy/views.py`.
 *
 * The `chat` tab has no single capability: it needs `ai.chat.document`
 * when a specific document is active (the "prepare and discuss a
 * document" flow) or `ai.chat.matter` for matter-wide / collection chat.
 */
export const filterTabsByCapability = (
  tabs,
  { mode, hasCapability, hasActiveDocument = false } = {}
) => {
  if (mode === AI_PANE_MODE_MATTER_CHAT_ONLY) return tabs;
  if (typeof hasCapability !== "function") return tabs;
  return tabs.filter((tab) => {
    if (tab === "chat") {
      return hasActiveDocument
        ? hasCapability("ai.chat.document")
        : hasCapability("ai.chat.matter");
    }
    const required = CAPABILITY_BY_TAB[tab];
    return !required || hasCapability(required);
  });
};

const LEGACY_CHATBOX_FLAG = "chatbox";
const AI_PANE_PARAM = "ai";

const isValidTab = (value) =>
  typeof value === "string" && AI_PANE_TABS.includes(value);

/**
 * Coerce an arbitrary tab value into one that's valid for the given
 * AIPane mode. Used by AIPane to handle URLs like `?ai=chat` arriving
 * on the inbox surface (which has no chat tab) — the tab falls back
 * to the mode's default rather than rendering nothing.
 *
 * Returns the input tab when it's already valid for the mode, the
 * mode's first tab otherwise.
 */
export const clampTabToMode = (tab, mode) => {
  const validTabs = getValidTabsForMode(mode);
  if (typeof tab === "string" && validTabs.includes(tab)) return tab;
  return validTabs[0] || AI_PANE_DEFAULT_TAB;
};

/**
 * Resolve which AI tab (if any) should be active for a given location/search.
 * Accepts either a `URLSearchParams`, a search string ("?ai=briefing"), or
 * the raw `location.search` value. Returns one of "briefing" | "analysis" |
 * "chat" or null when the panel is closed.
 */
export const getAIPaneTab = (input) => {
  let params;
  if (input instanceof URLSearchParams) {
    params = input;
  } else if (typeof input === "string") {
    params = new URLSearchParams(input.startsWith("?") ? input.slice(1) : input);
  } else if (input && typeof input === "object" && "search" in input) {
    params = new URLSearchParams(input.search || "");
  } else {
    params = new URLSearchParams("");
  }

  const explicit = params.get(AI_PANE_PARAM);
  if (isValidTab(explicit)) return explicit;

  // Legacy alias: ?chatbox=true → chat tab.
  if (params.get(LEGACY_CHATBOX_FLAG) === "true") return "chat";

  return null;
};

export const isAIPaneOpen = (input) => getAIPaneTab(input) !== null;

/**
 * Build a search string that opens the AI panel on the given tab. Preserves
 * any existing query params on the input search. Strips the legacy
 * `?chatbox=true` flag when writing so the URL doesn't carry both.
 */
export const buildAIPaneSearch = (currentSearch, tab) => {
  const targetTab = isValidTab(tab) ? tab : AI_PANE_DEFAULT_TAB;
  const base =
    typeof currentSearch === "string"
      ? currentSearch.startsWith("?")
        ? currentSearch.slice(1)
        : currentSearch
      : "";
  const params = new URLSearchParams(base);
  params.set(AI_PANE_PARAM, targetTab);
  params.delete(LEGACY_CHATBOX_FLAG);
  const str = params.toString();
  return str ? `?${str}` : "";
};

/**
 * Build a search string that closes the AI panel — strips both the new
 * `?ai=` param and the legacy `?chatbox=true` flag.
 */
export const buildClosedAIPaneSearch = (currentSearch) => {
  const base =
    typeof currentSearch === "string"
      ? currentSearch.startsWith("?")
        ? currentSearch.slice(1)
        : currentSearch
      : "";
  const params = new URLSearchParams(base);
  params.delete(AI_PANE_PARAM);
  params.delete(LEGACY_CHATBOX_FLAG);
  const str = params.toString();
  return str ? `?${str}` : "";
};

/**
 * Append `?ai=<tab>` (or preserve a legacy `?chatbox=true`) to a path that
 * already has its own query string. Used by navigationUrl-style callers
 * that build URLs from path fragments and need the AI-pane state to ride
 * along with cross-document navigation.
 */
export const appendAIPaneTo = (path, currentSearch) => {
  const tab = getAIPaneTab(currentSearch);
  if (!tab) return path;
  const search = buildAIPaneSearch("", tab);
  if (!search) return path;
  if (path.includes("?")) return `${path}&${search.slice(1)}`;
  return `${path}${search}`;
};

/**
 * `appendAIPaneTo` for callers with no router hook in scope — plain utils
 * and components that take `navigate` as a prop and render outside a
 * <Router> in their tests. Reads the live URL, which under BrowserRouter
 * is the same value `useLocation().search` would return. A no-op when the
 * AI panel is closed.
 */
export const appendAIPaneFromCurrentUrl = (path) =>
  appendAIPaneTo(
    path,
    typeof window !== "undefined" ? window.location.search : ""
  );

/**
 * Append (or rewrite) `?ai=<tab>` on a path, forcing a specific tab
 * regardless of the path's existing AI-pane state. Used by the Phase 2
 * Analyze affordances (header button, viewer More menu, row More menu)
 * that always want to land the user on the Analysis tab, even if the
 * source URL had `?ai=chat` or no AI param at all.
 *
 * Differs from `appendAIPaneTo` (which preserves the *current* tab so
 * cross-document navigation feels stable). Differs from `buildAIPaneSearch`
 * which only operates on a search string, not a full path.
 */
export const buildPathWithAIPaneTab = (path, tab) => {
  if (typeof path !== "string") return path;
  const [base, existingSearch = ""] = path.split("?", 2);
  const nextSearch = buildAIPaneSearch(
    existingSearch ? `?${existingSearch}` : "",
    tab
  );
  return `${base}${nextSearch}`;
};

/**
 * Pull the active document ID out of a matter-workspace URL. Matter URLs
 * look like `/matter/{matterId}/(F<id>/)*D<id>` — only the trailing `D`
 * segment is a document. Returns `null` for matter-root or folder-only
 * URLs so callers can render an empty state instead of fetching for a
 * non-existent document.
 */
export const getActiveDocumentIdFromPath = (pathname) => {
  if (typeof pathname !== "string" || !pathname.startsWith("/matter/")) return null;
  const segments = pathname.split("/matter/")[1]?.split("/").filter(Boolean) || [];
  const last = segments[segments.length - 1];
  if (!last || !last.startsWith("D")) return null;
  const id = last.slice(1);
  return id || null;
};

export const getMatterIdFromPath = (pathname) => {
  if (typeof pathname !== "string" || !pathname.startsWith("/matter/")) return null;
  const segments = pathname.split("/matter/")[1]?.split("/").filter(Boolean) || [];
  return segments[0] || null;
};

/**
 * Smart default for "user just clicked the AI button — which tab should
 * open?". The fallback depends on mode and whether the current pathname
 * points at a specific document:
 *   - matter mode + active document → "analysis" (rendered as "The
 *     Brief" — the per-document surface; replaces the pre-2026-04-29
 *     "briefing" default that pointed at the now-removed Briefing tab).
 *   - matter mode at matter root    → "chat" (only needs a matter
 *     context, so the panel is still useful at the matter root).
 *   - inbox mode                    → "briefing" (chat doesn't exist on
 *     the inbox surface; the inbox always pairs the AI rail with a
 *     selected document anyway, but we still need a sensible default
 *     if the user hits the toggle before picking a row).
 */
export const pickDefaultAIPaneTab = (pathname, mode = AI_PANE_MODE_MATTER) => {
  if (mode === AI_PANE_MODE_INBOX) return "briefing";
  if (mode === AI_PANE_MODE_MATTER_CHAT_ONLY) return "chat";
  if (getActiveDocumentIdFromPath(pathname)) return "analysis";
  return "chat";
};
